import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraIssueEdgesReference$key } from '@atlassian/jira-relay/src/__generated__/screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraIssueEdgesReference.graphql';
import type { screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraVersionReference$key } from '@atlassian/jira-relay/src/__generated__/screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraVersionReference.graphql';
import { IssuesWarningCountProvider } from '@atlassian/jira-software-releases-version-issue-warning-controller';

type Props = {
	jiraIssueEdgesReference: screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraIssueEdgesReference$key;
	jiraVersionReference: screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraVersionReference$key;
};

export const IssueListScreenAnalytics = ({
	jiraIssueEdgesReference,
	jiraVersionReference,
}: Props) => {
	const jiraIssueEdgeReferenceData = useFragment(
		graphql`
			fragment screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraIssueEdgesReference on JiraIssueEdge
			@relay(plural: true) {
				node {
					devOpsSummarisedEntities @skip(if: $isDevopsFeatureDisabledInFedRamp) {
						summarisedDeployments {
							count
						}
						summarisedFeatureFlags {
							totalCount
						}
						summarisedBuilds {
							count
						}
					}
					devSummaryCache @skip(if: $isDevopsFeatureDisabledInFedRamp) {
						devSummary {
							pullrequest {
								overall {
									count
								}
							}
							branch {
								overall {
									count
								}
							}
							review {
								overall {
									count
								}
							}
							commit {
								overall {
									count
								}
							}
							build {
								overall {
									count
								}
							}
							deploymentEnvironments {
								overall {
									topEnvironments {
										status
									}
								}
							}
						}
					}
				}
				...ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_issueEdges
			}
		`,
		jiraIssueEdgesReference,
	);

	const warningConfigFragment = useFragment(
		graphql`
			fragment screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraVersionReference on JiraVersion {
				...ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_jiraVersion
			}
		`,
		jiraVersionReference,
	);

	const nodes = jiraIssueEdgeReferenceData.map((edge) => edge.node).filter(Boolean);

	// TODO: This will be improved by getting these values from the server either from Data Depot
	// or ARI Graph Store relationships. The current code has a bug that might result incorrect
	// boolean due to pagination. Also this is ugly to calculate in FE.
	const hasBambooDeployment = nodes.some(
		(node) =>
			(
				node.devSummaryCache?.devSummary?.deploymentEnvironments?.overall?.topEnvironments ?? []
			).filter((deployment) => deployment.status === 'DEPLOYED').length > 0,
	);
	const hasDeployments = nodes.some(
		(node) =>
			node.devOpsSummarisedEntities?.summarisedDeployments?.count != null &&
			node.devOpsSummarisedEntities?.summarisedDeployments?.count > 0,
	);
	const hasFeatureFlags = nodes.some(
		(node) => (node.devOpsSummarisedEntities?.summarisedFeatureFlags?.totalCount ?? 0) > 0,
	);
	const hasCommits = nodes.some(
		(node) => Number(node.devSummaryCache?.devSummary?.commit?.overall?.count) > 0,
	);
	const hasPullRequests = nodes.some(
		(node) => Number(node.devSummaryCache?.devSummary?.pullrequest?.overall?.count) > 0,
	);
	const hasBranches = nodes.some(
		(node) => Number(node.devSummaryCache?.devSummary?.branch?.overall?.count) > 0,
	);
	const hasBuilds = nodes.some(
		(node) =>
			Number(node.devSummaryCache?.devSummary?.build?.overall?.count) > 0 ||
			Number(node.devOpsSummarisedEntities?.summarisedBuilds?.count) > 0,
	);
	const hasReviews = nodes.some(
		(node) => Number(node.devSummaryCache?.devSummary?.review?.overall?.count) > 0,
	);

	return (
		<IssuesWarningCountProvider
			jiraVersion={warningConfigFragment}
			issueEdges={jiraIssueEdgeReferenceData}
		>
			{(warningCount) => (
				<FireScreenAnalytics
					attributes={{
						hasBamboo: hasBambooDeployment,
						hasBranches,
						hasBuilds,
						hasCommits,
						hasDeployments,
						hasFeatureFlags,
						hasPullRequests,
						hasReviews,
						warningCount,
					}}
				/>
			)}
		</IssuesWarningCountProvider>
	);
};
